import React, { useState, useEffect } from 'react';
import { ref, deleteObject } from 'firebase/storage';
import { storage } from '../../firebase';
import logger from '../../utils/logger';
import { toast } from 'react-toastify';
import { TrashIcon, MagnifyingGlassIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import PhotoViewerModal from './PhotoViewerModal';

/**
 * PhotoGallery Component
 *
 * Displays uploaded photos organized by category with thumbnails
 * Supports viewing, downloading, and deleting photos
 *
 * Props:
 * - entityType: 'vehicle' | 'job'
 * - entityId: Vehicle ID or Job Card Number
 * - photos: Array of photo metadata from Firestore
 * - onPhotosUpdate: Callback when photos are deleted
 * - readOnly: Disable delete functionality (default: false)
 */

const PhotoGallery = ({
  entityType,
  entityId,
  photos = [],
  onPhotosUpdate,
  readOnly = false
}) => {
  const [photosByCategory, setPhotosByCategory] = useState({});
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);

  // Group photos by category
  useEffect(() => {
    const grouped = photos.reduce((acc, photo) => {
      const category = photo.category || 'Uncategorized';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(photo);
      return acc;
    }, {});

    // Sort photos within each category by date (newest first)
    Object.keys(grouped).forEach(category => {
      grouped[category].sort((a, b) =>
        new Date(b.uploadedAt) - new Date(a.uploadedAt)
      );
    });

    setPhotosByCategory(grouped);
  }, [photos]);

  // Handle photo deletion
  const handleDelete = async (photo) => {
    if (!window.confirm('Are you sure you want to delete this photo? This action cannot be undone.')) {
      return;
    }

    try {
      setIsLoading(true);

      // Delete original
      const originalRef = ref(storage, photo.storagePath);
      await deleteObject(originalRef);

      // Try to delete thumbnail (may not exist if Cloud Function hasn't run yet)
      if (photo.thumbUrl) {
        try {
          const thumbPath = photo.storagePath.replace('original_', 'thumb_');
          const thumbRef = ref(storage, thumbPath);
          await deleteObject(thumbRef);
        } catch (e) {
          logger.info('Thumbnail not found or already deleted');
        }
      }

      // Try to delete medium size
      if (photo.mediumUrl) {
        try {
          const mediumPath = photo.storagePath.replace('original_', 'medium_');
          const mediumRef = ref(storage, mediumPath);
          await deleteObject(mediumRef);
        } catch (e) {
          logger.info('Medium image not found or already deleted');
        }
      }

      toast.success('Photo deleted successfully');

      // Update parent component
      if (onPhotosUpdate) {
        const updatedPhotos = photos.filter(p => p.storagePath !== photo.storagePath);
        onPhotosUpdate(updatedPhotos);
      }
    } catch (error) {
      logger.error('Error deleting photo:', error);
      toast.error('Failed to delete photo');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle photo download
  const handleDownload = async (photo) => {
    try {
      const link = document.createElement('a');
      link.href = photo.originalUrl;
      link.download = photo.fileName || 'photo.jpg';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Download started');
    } catch (error) {
      logger.error('Error downloading photo:', error);
      toast.error('Failed to download photo');
    }
  };

  // Open photo in viewer
  const handleView = (photo) => {
    setSelectedPhoto(photo);
    setViewerOpen(true);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (photos.length === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-8 sm:p-12 text-center">
        <div className="text-5xl sm:text-6xl mb-4">📷</div>
        <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-2">
          No Photos Yet
        </h3>
        <p className="text-xs sm:text-sm text-gray-500">
          Photos uploaded for this {entityType} will appear here
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-base sm:text-lg font-semibold text-gray-800">
          Photo Gallery ({photos.length} photo{photos.length > 1 ? 's' : ''})
        </h3>
      </div>

      {/* Photos organized by category */}
      {Object.entries(photosByCategory).map(([category, categoryPhotos]) => (
        <div key={category} className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-3 sm:px-4 py-2.5 sm:py-3">
            <h4 className="text-white font-semibold flex items-center justify-between text-sm sm:text-base">
              <span className="truncate pr-2">{category}</span>
              <span className="text-blue-200 text-xs sm:text-sm flex-shrink-0">
                {categoryPhotos.length} photo{categoryPhotos.length > 1 ? 's' : ''}
              </span>
            </h4>
          </div>

          <div className="p-3 sm:p-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
              {categoryPhotos.map((photo, index) => (
                <div
                  key={photo.storagePath || index}
                  className="group relative bg-gray-100 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                >
                  {/* Photo thumbnail */}
                  <div
                    className="aspect-square cursor-pointer"
                    onClick={() => handleView(photo)}
                  >
                    <img
                      src={photo.thumbUrl || photo.mediumUrl || photo.originalUrl}
                      alt={`${category} ${index + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => {
                        // Fallback to original if thumbnail fails
                        if (e.target.src !== photo.originalUrl) {
                          e.target.src = photo.originalUrl;
                        }
                      }}
                    />
                  </div>

                  {/* Hover overlay with actions (desktop) / Visible buttons (mobile) */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 sm:group-hover:bg-opacity-60 transition-opacity flex items-center justify-center opacity-0 sm:group-hover:opacity-100">
                    <div className="flex space-x-1.5 sm:space-x-2">
                      <button
                        onClick={() => handleView(photo)}
                        className="p-1.5 sm:p-2 bg-white rounded-full text-gray-800 hover:bg-gray-100 transition-colors"
                        title="View full size"
                      >
                        <MagnifyingGlassIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                      </button>

                      <button
                        onClick={() => handleDownload(photo)}
                        className="p-1.5 sm:p-2 bg-white rounded-full text-gray-800 hover:bg-gray-100 transition-colors"
                        title="Download"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                      </button>

                      {!readOnly && (
                        <button
                          onClick={() => handleDelete(photo)}
                          disabled={isLoading}
                          className="p-1.5 sm:p-2 bg-red-500 rounded-full text-white hover:bg-red-600 transition-colors disabled:opacity-50"
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Mobile action buttons - always visible on small screens */}
                  <div className="sm:hidden absolute top-1 right-1 flex space-x-1">
                    <button
                      onClick={() => handleView(photo)}
                      className="p-1.5 bg-white bg-opacity-90 rounded-full text-gray-800 shadow-md"
                      title="View"
                    >
                      <MagnifyingGlassIcon className="h-4 w-4" />
                    </button>
                    {!readOnly && (
                      <button
                        onClick={() => handleDelete(photo)}
                        disabled={isLoading}
                        className="p-1.5 bg-red-500 bg-opacity-90 rounded-full text-white shadow-md disabled:opacity-50"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  {/* Date badge */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-1.5 sm:p-2">
                    <p className="text-white text-[10px] sm:text-xs font-medium truncate">
                      {formatDate(photo.uploadedAt)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}

      {/* Photo Viewer Modal */}
      {selectedPhoto && (
        <PhotoViewerModal
          isOpen={viewerOpen}
          onClose={() => {
            setViewerOpen(false);
            setSelectedPhoto(null);
          }}
          photo={selectedPhoto}
          allPhotos={photos}
          onDelete={!readOnly ? handleDelete : null}
          onDownload={handleDownload}
        />
      )}
    </div>
  );
};

export default PhotoGallery;
